import { Link } from "react-router-dom";
import { useState } from "react";
import { Nav } from "../components/Nav";
import { Footer } from "../components/Footer";
import { Reveal } from "../components/Reveal";
import { IMAGES, PILLARS, WORLDS } from "../data/content";
import { StreamsShowcase } from "../components/StreamsShowcase";
import { Testimonials } from "../components/Testimonials";
import { WelcomePlayer } from "../components/WelcomePlayer";
import { BooksShowcase } from "../components/BooksShowcase";
import { BrandsStrip } from "../components/BrandsStrip";
import WelcomePopup from "../components/WelcomePopup";

const Doorway = ({ w }) => {
  const [hover, setHover] = useState(false);
  return (
    <Link to={`/${w.slug}`} data-testid={`doorway-${w.slug}`}
      onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
      className={`relative overflow-hidden border border-[#21302A]/10 flex flex-col justify-between p-8 min-h-[340px] transition-all duration-500 group ${
        hover ? "bg-[#1E3A2B] text-[#FAF4E9]" : "bg-[#FAF4E9] text-[#21302A]"
      }`}>
      <span className={`absolute top-0 left-0 h-1 bg-[#C77E88] transition-all duration-500 ${hover ? "w-full" : "w-0"}`} />
      <div className="flex items-center justify-between">
        <span className={`eyebrow ${hover ? "text-[#DCBB6C]" : ""}`}>{w.n} — {w.verb}</span>
      </div>
      <div>
        <h3 className={`font-serif-d text-2xl md:text-3xl leading-tight ${hover ? "text-[#FAF4E9]" : "text-[#1E3A2B]"}`}>{w.title}</h3>
        <p className={`text-sm mt-3 ${hover ? "text-[#FAF4E9]/75" : "text-[#21302A]/65"}`}>{w.blurb}</p>
        <span className={`inline-block mt-6 text-xs uppercase tracking-[0.2em] ${hover ? "text-[#DCBB6C]" : "text-[#C0953A]"}`}>Enter →</span>
      </div>
    </Link>
  );
};

export default function Landing() {
  return (
    <div className="bg-[#FAF4E9] min-h-screen overflow-hidden">
      <Nav />
      <WelcomePopup />

      {/* Hero */}
      <section className="relative pt-36 pb-20 px-6">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7 rise">
            <p className="eyebrow">Meet Coach Nata</p>
            <h1 className="font-serif-d text-[#1E3A2B] text-5xl sm:text-6xl lg:text-7xl tracking-tighter leading-[0.95] mt-5">
              I build <span className="italic-emph text-[#C0953A]">ecosystems</span> for transformation.
            </h1>
            <p className="text-[#21302A]/75 text-lg mt-8 max-w-xl font-sans-d leading-relaxed">
              Known as "The BounceBack Queen." From poverty in North Philadelphia to a full scholarship at Haverford —
              I learned that overcoming was never the finish line. <span className="text-[#1E3A2B] font-semibold">Overflow is.</span>
            </p>
            <div className="flex flex-wrap gap-4 mt-10">
              <Link to="/assessment" data-testid="hero-assessment-btn"
                className="bg-[#1E3A2B] text-[#FAF4E9] hover:bg-[#2E5240] transition-colors px-8 py-4 uppercase tracking-widest text-xs font-medium">
                Take the LifePrints™ Assessment →
              </Link>
              <Link to="/transform" data-testid="hero-explore-btn"
                className="border border-[#1E3A2B] text-[#1E3A2B] hover:bg-[#1E3A2B]/5 transition-colors px-8 py-4 uppercase tracking-widest text-xs font-medium">
                Explore the Hub
              </Link>
            </div>
            <p className="mt-10 text-xs uppercase tracking-[0.2em] text-[#21302A]/50">Focus · Faith · Strategy · Fun</p>
          </div>
          <div className="lg:col-span-5 rise" style={{ animationDelay: "0.2s" }}>
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-br from-[#E7AEB4] to-[#C77E88] -z-10 translate-x-4 translate-y-4" />
              <div className="absolute -top-5 -left-5 w-24 h-24 border-2 border-[#C0953A] -z-10" />
              <div className="bg-gradient-to-b from-[#F8E9EC] to-[#FAF4E9] p-2">
                <img src={IMAGES.hero} alt="Coach Nata" className="w-full aspect-[4/5] object-cover object-top" />
              </div>
              <div className="absolute -bottom-5 -right-5 bg-[#1E3A2B] text-[#FAF4E9] px-5 py-3">
                <p className="font-serif-d italic-emph text-lg leading-none text-[#DCBB6C]">The BounceBack Queen</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Marquee */}
      <div className="border-y border-[#21302A]/10 py-6 overflow-hidden">
        <div className="marquee-track">
          {[...Array(2)].map((_, k) => (
            <span key={k} className="font-serif-d text-3xl md:text-4xl text-[#1E3A2B]/15 px-8 italic-emph">
              Overcome & Overflow • Break the Box • The BounceBack Queen • Healed → Whole • Built for More •&nbsp;
            </span>
          ))}
        </div>
      </div>

      {/* Welcome message player */}
      <WelcomePlayer />

      {/* Many Streams interactive showcase */}
      <StreamsShowcase />

      {/* Four Pillars */}
      <section className="max-w-7xl mx-auto px-6 py-28">
        <Reveal><p className="eyebrow">The Four Pillars</p>
          <h2 className="font-serif-d text-4xl md:text-5xl text-[#1E3A2B] tracking-tight mt-3 max-w-2xl">Everything I teach, I <span className="italic-emph text-[#C0953A]">walked through</span> first.</h2></Reveal>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8 mt-16">
          {PILLARS.map((p, i) => (
            <Reveal key={p.title} delay={i * 0.1}>
              <div className="group border-t-2 border-[#21302A]/10 hover:border-[#C77E88] pt-6 transition-all duration-300 hover:-translate-y-2">
                <span className="font-serif-d italic-emph text-5xl text-[#C0953A] group-hover:text-[#C77E88] transition-colors">{p.n}</span>
                <h3 className="font-serif-d text-2xl text-[#1E3A2B] mt-4">{p.title}</h3>
                <p className="text-[#21302A]/65 text-sm mt-3 leading-relaxed">{p.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* The Hub */}
      <section className="bg-[#FAF4E9] px-6 pb-28">
        <div className="max-w-7xl mx-auto">
          <Reveal><p className="eyebrow">The Hub</p>
            <h2 className="font-serif-d text-4xl md:text-5xl text-[#1E3A2B] tracking-tight mt-3">Five worlds, <span className="italic-emph text-[#C0953A]">one</span> mission.</h2>
            <p className="text-[#21302A]/65 mt-4 max-w-lg">Everything I do lives here. Step into whichever doorway you need today.</p></Reveal>
          <div className="grid md:grid-cols-3 lg:grid-cols-5 gap-4 mt-12">
            {WORLDS.map((w) => <Doorway key={w.slug} w={w} />)}
          </div>
        </div>
      </section>

      {/* Featured books with real covers */}
      <BooksShowcase />

      {/* Testimonials */}
      <Testimonials />

      {/* Pink CTA band */}
      <section className="px-6 py-20 bg-gradient-to-r from-[#C77E88] to-[#E7AEB4]">
        <div className="max-w-5xl mx-auto flex flex-col md:flex-row items-center justify-between gap-8">
          <div>
            <p className="eyebrow text-[#1E3A2B]">Built for more than survival</p>
            <h2 className="font-serif-d text-3xl md:text-4xl text-[#1E3A2B] tracking-tight mt-2">Where are you <span className="italic-emph">right now?</span></h2>
            <p className="text-[#1E3A2B]/70 mt-3 max-w-md">Take the 10-domain LifePrints™ map and get a personalized report from Coach Nata.</p>
          </div>
          <Link to="/assessment" data-testid="pink-cta-btn"
            className="shrink-0 bg-[#1E3A2B] text-[#FAF4E9] hover:bg-[#132619] transition-colors px-8 py-4 uppercase tracking-widest text-xs font-semibold">
            Map My Life →
          </Link>
        </div>
      </section>

      {/* Story band */}
      <section className="bg-[#1E3A2B] px-6 py-28">
        <div className="max-w-5xl mx-auto text-center">
          <Reveal>
            <p className="eyebrow text-[#DCBB6C]">My Story</p>
            <p className="font-serif-d text-3xl md:text-4xl text-[#FAF4E9] leading-snug mt-6">
              "My purpose is to help others tap into theirs — and to help them overcome the barriers necessary to do so."
            </p>
            <p className="mt-8 text-[#DCBB6C] uppercase tracking-[0.2em] text-xs">— Coach Nata</p>
            <Link to="/contact" data-testid="story-cta"
              className="inline-block mt-10 bg-[#C0953A] text-[#132619] hover:bg-[#DCBB6C] transition-colors px-8 py-4 uppercase tracking-widest text-xs font-semibold">
              Work With Coach Nata →
            </Link>
          </Reveal>
        </div>
      </section>

      {/* Ministries & brands */}
      <BrandsStrip />

      <Footer />
    </div>
  );
}
